import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

const THUMBNAIL_SIZE = 128;

const thumbnailCache = new Map<string, string>();
let thumbnailListeners: Array<() => void> = [];
const renderQueue: Array<{ modelId: string; url: string }> = [];
let isRendering = false;
let offscreenRenderer: THREE.WebGLRenderer | null = null;
let renderCanvas: HTMLCanvasElement | null = null;
let loader: GLTFLoader | null = null;

function notifyThumbnailListeners() {
  for (const fn of thumbnailListeners) fn();
}

function disposeOffscreenRenderer() {
  if (offscreenRenderer) {
    offscreenRenderer.dispose();
    offscreenRenderer.forceContextLoss();
    offscreenRenderer = null;
  }
  if (renderCanvas) {
    renderCanvas = null;
  }
  loader = null;
}

function ensureRenderer() {
  if (!renderCanvas) {
    renderCanvas = document.createElement("canvas");
    renderCanvas.width = THUMBNAIL_SIZE;
    renderCanvas.height = THUMBNAIL_SIZE;
  }
  if (!offscreenRenderer) {
    offscreenRenderer = new THREE.WebGLRenderer({
      canvas: renderCanvas,
      antialias: true,
      alpha: true,
    });
    offscreenRenderer.setSize(THUMBNAIL_SIZE, THUMBNAIL_SIZE, false);
    offscreenRenderer.toneMapping = THREE.NoToneMapping;
  }
  if (!loader) {
    loader = new GLTFLoader();
  }
}

async function renderOneThumbnail(modelId: string, url: string): Promise<void> {
  try {
    ensureRenderer();

    const gltf = await loader!.loadAsync(url);
    const model = gltf.scene;

    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 0.8));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1);
    dirLight.position.set(2, 3, 2);
    scene.add(dirLight);

    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    model.position.sub(center);
    scene.add(model);

    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const radius = maxDim / 2;
    const fov = 40;
    const dist = (radius / Math.tan((fov * Math.PI) / 360)) * 1.3;

    const camera = new THREE.PerspectiveCamera(
      fov,
      1,
      Math.max(0.01, dist * 0.01),
      dist * 10,
    );
    camera.position.set(dist * 0.7, dist * 0.5, dist * 0.7);
    camera.lookAt(0, 0, 0);

    offscreenRenderer!.render(scene, camera);

    const dataUrl = renderCanvas!.toDataURL("image/png");
    thumbnailCache.set(modelId, dataUrl);

    scene.remove(model);
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];
        for (const mat of materials) mat?.dispose();
      }
    });
  } catch {
    // Thumbnail generation failed; component will show fallback icon
  }
}

async function processRenderQueue(): Promise<void> {
  if (isRendering) return;
  isRendering = true;

  while (renderQueue.length > 0) {
    const item = renderQueue.shift()!;
    if (!thumbnailCache.has(item.modelId)) {
      await renderOneThumbnail(item.modelId, item.url);
      notifyThumbnailListeners();
    }
  }

  isRendering = false;
  disposeOffscreenRenderer();
}

/** Subscribe to cache updates. Returns an unsubscribe function. */
export function subscribeThumbnails(listener: () => void): () => void {
  thumbnailListeners.push(listener);
  return () => {
    thumbnailListeners = thumbnailListeners.filter((fn) => fn !== listener);
  };
}

export function getCachedThumbnail(modelId: string): string | undefined {
  return thumbnailCache.get(modelId);
}

export function enqueueThumbnail(modelId: string, url: string): void {
  if (thumbnailCache.has(modelId)) return;
  if (renderQueue.some((item) => item.modelId === modelId)) return;
  renderQueue.push({ modelId, url });
  processRenderQueue();
}
